import type { HelixRequest, ModuleRouteVariables } from "../contract";
import type { CommercialFailureReason } from "../../../contracts/values";

const START_COMMERCIAL_URL = "https://api.twitch.tv/helix/channels/commercial";

/** Twitch's own accepted lengths (seconds) for Start Commercial. */
export const COMMERCIAL_LENGTHS = [30, 60, 90, 120, 150, 180] as const;
export type CommercialLength = (typeof COMMERCIAL_LENGTHS)[number];

export interface CommercialResult {
  started: boolean;
  reason: CommercialFailureReason | "unauthorized" | null;
  detail: Readonly<Record<string, string | number | boolean | null>>;
  length: number | null;
  message: string | null;
  retryAfter: number | null;
  rateLimitRemaining: number | null;
}

type GetAppAccessToken = ModuleRouteVariables["getAppAccessToken"];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const textOrNull = (value: unknown): string | null =>
  typeof value === "string" && value.length > 0 ? value : null;

const numberOrNull = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const failure = (
  reason: CommercialFailureReason | "unauthorized",
  detail: Readonly<Record<string, string | number | boolean | null>> = {},
  rateLimitRemaining: number | null = null,
): CommercialResult => ({ started: false, reason, detail, length: null, message: null, retryAfter: null, rateLimitRemaining });

const missingScopeMessage = (message: string | null): boolean =>
  message !== null && /(missing|lacks?|does not have).*(scope|permission)|(scope|permission).*(missing|required)/i.test(message);

const invalidTokenMessage = (message: string | null): boolean =>
  message !== null && /(?:invalid|expired|revoked).{0,30}(?:oauth )?token|(?:oauth )?token.{0,30}(?:invalid|expired|revoked|not valid)/i.test(message);

/** The status-to-reason mapping is this endpoint's own business meaning, not `helixRequest`'s. */
const commercialReasonFor = (status: number, message: string | null): CommercialFailureReason => {
  if (status === 429) return "rate_limited";
  if (status === 401) {
    if (missingScopeMessage(message)) return "scope_missing";
    if (invalidTokenMessage(message)) return "token_invalid";
    return "twitch_error";
  }
  if (status === 400 && message !== null && /offline|not live|must be live/i.test(message)) return "stream_offline";
  return "twitch_error";
};

/** Starts a commercial on the app token, the same path as the ads snooze action. */
export const startCommercial = async (
  environment: Env,
  channelId: string,
  length: CommercialLength,
  now: string,
  getAppAccessToken: GetAppAccessToken,
  isManagementAuthorized: () => Promise<boolean>,
  helixRequest: HelixRequest,
  fetcher: typeof fetch = fetch,
): Promise<CommercialResult> => {
  let accessToken: string;
  try {
    accessToken = await getAppAccessToken(environment, now, fetcher);
  } catch (error: unknown) {
    return failure("app_token_unavailable", { message: error instanceof Error ? error.message : String(error) });
  }

  if (!await isManagementAuthorized()) return failure("unauthorized", { status: null, message: null });

  const request = (token: string) => helixRequest<Record<string, unknown>>({
    method: "POST",
    url: START_COMMERCIAL_URL,
    body: { broadcaster_id: channelId, length },
    accessToken: token,
    clientId: environment.TWITCH_CLIENT_ID,
    fetcher,
  });
  let result = await request(accessToken);
  if (!result.ok && result.status === 401 && !missingScopeMessage(result.message)) {
    try {
      accessToken = await getAppAccessToken(environment, now, fetcher, true);
    } catch (error: unknown) {
      return failure("app_token_unavailable", { message: error instanceof Error ? error.message : String(error) });
    }
    if (!await isManagementAuthorized()) return failure("unauthorized", { status: null, message: null });
    result = await request(accessToken);
  }
  if (!result.ok) {
    if (result.reason === "timeout" || result.reason === "network_error") {
      return failure(result.reason, { status: null, twitchMessage: result.message });
    }
    return failure(commercialReasonFor(result.status ?? 0, result.message), {
      status: result.status,
      twitchMessage: result.message,
      ...(result.status === 429 && result.rateLimitRemaining === 0 && result.retryAfter != null ? { retryAfter: result.retryAfter } : {}),
    }, result.status === 429 ? result.rateLimitRemaining ?? null : null);
  }

  const body = isRecord(result.data) ? result.data : {};
  const first = Array.isArray(body.data) && isRecord(body.data[0]) ? body.data[0] : {};
  return {
    started: true,
    reason: null,
    detail: { status: result.status },
    length: numberOrNull(first.length),
    message: textOrNull(first.message),
    retryAfter: numberOrNull(first.retry_after),
    rateLimitRemaining: null,
  };
};
